import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { AlertController, ModalController, NavController, ToastController } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { ProductViewComponent } from "src/app/components/product-view/product-view.component";
import { ProductModel } from "src/app/models/product.model";
import { ProfileModel } from "src/app/models/profile.model";
import { TourneeModel } from "src/app/models/tournee.model";
import { DataService } from "src/app/services/data/data.service";
import { MathService } from "src/app/services/math/math.service";
import { ProductService } from "src/app/services/product/product.service";
import { ProductStatsComponent } from "../product-stats/product-stats.component";
import { StockEditComponent } from "../stock-edit/stock-edit.component";

@Component({
  selector: "products-list",
  templateUrl: "./products-list.component.html",
  styleUrls: ["./products-list.component.scss"]
})
export class ProductsListComponent implements OnInit {
  @Input()
  title: string;

  @Input()
  subtitle: string;

  @Input()
  mode: string; // edit, view, order, catalog, sale, prepare, stat

  @Input()
  loading = false;

  @Input()
  size = 'large';

  @Input()
  canEdit = false;

  @Input()
  max: number;

  modal:HTMLIonModalElement

  reordering = false;

  @Input("items")
  set items(value: any) {
    this._items = value;
    this.itemsFiltered = value;
  }
  _items: ProductModel[] = []
  itemsFiltered: ProductModel[];

  @Input()
  sortBy: string;

  @Input()
  priceMode = 'VATIncluded';

  @Input()
  countryCode = 'FRA'; // format VAT.json / getTourneeCountryCode (3 lettres), pas 'FR'

  @Input()
  tournee: TourneeModel;

  @Input()
  statusName: string = 'status';

  @Input()
  onCancel = () => {};

  @Input()
  onValidate = () => {};

  @Input()
  showAdd = false;

  @Input()
  noClick = false;

  @Input()
  showSizings = false;

  @Input()
  showButtons = false;

  @Input()
  owner: ProfileModel;

  @Output() quantityChanged = new EventEmitter<ProductModel>();
  @Output() statusChanged = new EventEmitter<ProductModel>();
  @Output() productAddedToCatalog = new EventEmitter<ProductModel>();
  @Output() priceUpdated = new EventEmitter<ProductModel>();
  @Output() stockUpdated = new EventEmitter<ProductModel>();
  @Output() clicked = new EventEmitter<ProductModel>();

  @Output() addClicked = new EventEmitter();
  @Output() okClicked = new EventEmitter();
  @Output() cancelClicked = new EventEmitter();
  @Output() reOrdered = new EventEmitter<ProductModel>();
  @Output() iconUpdated = new EventEmitter<ProductModel>();

  visibilityAlertDisplayed = false;

  constructor(
    private dataService: DataService,
    private navCtrl: NavController,
    private alertCtrl: AlertController,
    public mathService: MathService,
    private modalCtrl: ModalController,
    private translateService: TranslateService,
    private productService:ProductService,
    private toastCtrl: ToastController,
  ) {}

  ngOnInit(): void {}

  ngOnDestroy(){
    this.closeToast();
  }

  closeToast(){
    if(typeof(this.toastCtrl) != 'undefined'){
          this.toastCtrl.dismiss();
    } 
  }

  public edit(item: ProductModel): void {
    this.navCtrl.navigateForward("/producer/" + this.owner.key + "/product/" + item.key + "/edit");
  }

  public add(): void {
    this.navCtrl.navigateForward("/producer/" + this.owner.key + "/product/new");
  }

  public publish(event, item: ProductModel): void {
    event.stopPropagation();
    this.changeProductStatus(item, "published");
    this.showVisibilityAlert();
  }

  public unpublish(event, item: ProductModel): void {
    event.stopPropagation();
    this.changeProductStatus(item, "unpublished");
    this.showVisibilityAlert();
  }


  showVisibilityAlert(){
    if(!this.visibilityAlertDisplayed){
      this.presentToast(this.translateService.instant('productCatalog.AlertVisibility'));

      this.visibilityAlertDisplayed = true;
    }
  }

  async presentToast(message) {
    const toast = await this.toastCtrl.create({
      message: message,
      duration:15000,
      buttons:[{
        side:'end',
        icon:'close-circle-outline',
        role:'cancel',
        handler:()=>{
          console.log('dismiss toast');
          //toast.dismiss();
        }
      }],
      position: 'bottom'
    });
    toast.present();
  }

  async changeProductStatus(product: ProductModel, status: string): Promise<void> {
    product[this.statusName] = status;
    return this.dataService.updateProductStatus(product);
  }

  async deleteProduct(item: ProductModel, i: number): Promise<void> {
    const alert = await this.alertCtrl.create({
      header: this.translateService.instant("buttons.Delete"),
      message: this.translateService.instant("productCatalog.ConfirmDeleteProductMessage"),
      buttons: [
        {
          text: this.translateService.instant("buttons.Cancel"),
          handler: data => {
            //alert.dismiss();
          }
        },
        {
          text: this.translateService.instant("buttons.Delete"),
          handler: data => {
            //alert.dismiss();
            this.dataService.deleteProduct(item).then(_ => {
              this.items.splice(i, 1);
            });
          }
        }
      ]
    });
    await alert.present();
  }

  async updatePrice(event,item: ProductModel): Promise<void> {
    if(!this.canEdit){
      return;
    }
    console.log('updatePrice');
    event.stopPropagation();
    const alert = await this.alertCtrl.create({
      header: this.translateService.instant("productCatalog.Price"),
      message: this.translateService.instant("productCatalog.PriceUpdateMessage"),
      inputs: [
        {
          type: "number",
          name: "price",
          value: item.details.unitPriceVATIncluded,
          placeholder: this.translateService.instant("productCatalog.UnitPriceVATIncluded")
        }
      ],
      buttons: [
        { role: "cancel", text: this.translateService.instant("buttons.Cancel") },
        { role: "save", text: this.translateService.instant("buttons.Save") }
      ]
    });
    await alert.present();
    const alertResult = await alert.onDidDismiss();
    if (alertResult.role === "save") {
      item.details.unitPriceVATIncluded = alertResult.data.values.price;
      this.priceUpdated.emit(item);
    }
  }
  async updateStock(event,item: ProductModel,type): Promise<void> {
    if(!this.canEdit){
      return;
    }
    event.stopPropagation();
    this.modal = await this.modalCtrl.create({
      component: StockEditComponent,
      cssClass: "small-modal",
      componentProps: {
        tournee: this.tournee,
        item: item,
        onCancel: () => {
          this.modal.dismiss();
        },
        onSave: () => {
          this.stockUpdated.emit(item);
          this.modal.dismiss();
        }
      }
    });
    return await this.modal.present();


    // const buttons = [
    //   { role: "cancel", text: this.translateService.instant("buttons.Cancel") }, 
    //   { role: "save", text: this.translateService.instant("buttons.Save") }];
    // if(item.stock){
    //   buttons.push({ role: "remove", text: this.translateService.instant("productCatalog.stock.RemoveStockManagement") })
    // }
    // const message = this.translateService.instant("productCatalog.stock.InitMessage.all");
    // const alert = await this.alertCtrl.create({
    //   header: this.translateService.instant("productCatalog.stock.Stock"),
    //   message: message,
    //   inputs: [
    //     {
    //       type: "number",
    //       name: "initial",
    //       value: item.stock?.initial||null,
    //       placeholder: this.translateService.instant("productCatalog.stock.Initial")+ ' '+ 
    //         this.translateService.instant("productCatalog.stock.in")+ ' ' +
    //         this.translateService.instant("quantityMetric.short.plural."+item.details.quantityMetric),
    //       // placeholder: this.translateService.instant("productCatalog.UnitPriceVATIncluded")
    //     },
    //     {
    //       type: "number",
    //       name: "current",
    //       value: item.stock?.current||null,
    //       placeholder: this.translateService.instant("productCatalog.stock.Current")+ ' '+ 
    //         this.translateService.instant("productCatalog.stock.in")+ ' ' +
    //         this.translateService.instant("quantityMetric.short.plural."+item.details.quantityMetric),
    //       // placeholder: this.translateService.instant("productCatalog.UnitPriceVATIncluded")
    //     }
    //   ],
    //   buttons: buttons
    // });
    // await alert.present();
    // const alertResult = await alert.onDidDismiss();
    // if (alertResult.role === "save") {
    //   if(!item.stock){
    //     item.stock = {
    //       initial: null,
    //       current: null
    //     }
    //   }
    //   item.stock.initial = alertResult.data.values.initial;
    //   item.stock.current = alertResult.data.values.current;
    //   if(alertResult.data.values.current <= 0){
    //     item.stockStatus = 'outOfStock'
    //     delete item.stock;
    //   }
    //   item.stockStatus = alertResult.data.values.current <= 0?'outOfStock':'available';
    //   console.log(item);
    //   this.stockUpdated.emit(item);
    // }
    // else if(alertResult.role === 'remove'){
    //   delete item.stock;
    //   this.stockUpdated.emit(item);
    // }
    // else if(alertResult.role === 'cancel'){
    //   if(item.stock.initial == null && item.stock.current == null){
    //     delete item.stock;
    //   }
    // }
  }

  async presentProduct(product: ProductModel): Promise<void> {
    const modal = await this.modalCtrl.create({
      component: ProductViewComponent,
      cssClass: "shopping-cart",
      componentProps: {
        item: product,
        priceMode: this.priceMode,
        countryCode: this.countryCode
      }
    });
    return await modal.present();
  }

  onFilterAndSearched(filter:any){
    this.itemsFiltered = this.productService.filterAndSearch(this._items,filter.keyword, filter.types);
  }

  onSorted(sortBy:string){
    const sortExclusions = {
      abc:['rank'],
      rank:[]
    }
    this.itemsFiltered = this._items.sort(this.productService.sortProduct(sortExclusions[sortBy]));
  }

  clickProduct(event,item: ProductModel): void {
    console.log('clickProduct',event,item,this.mode);
    if(this.noClick){ return }
    if (this.mode == "order" || this.mode == "view") {
      this.presentProduct(item);
    }  else if (this.mode == "catalog") {
      this.addToCatalog(item);
    } else if (this.mode == "edit") {
        this.edit(item);
    } else if (this.mode =="sale"){
      if(item.status == 'included'){
        this.removeToSale(event,item);
      }
      else{
        this.addToSale(event,item);
      }
    }
  }

  addProduct(){
    this.addClicked.emit();
  }

  addToCatalog(item: ProductModel): void {
    this.productAddedToCatalog.emit(item);
  }

  cancel(): void {
    this.cancelClicked.emit();
    this.onCancel();
  }

  validate(): void {
    this.okClicked.emit();
    this.onValidate();
  }

  addToSale(event,item: ProductModel): void {
    event.stopPropagation();
    item[this.statusName] = "included";
    this.statusChanged.emit(item);
  }
  removeToSale(event, item: ProductModel): void {
    event.stopPropagation();
    item[this.statusName] = "notIncluded";
    this.statusChanged.emit(item);
  }
  MakeAvailable(event:any,item: ProductModel): void {
    console.log('MakeAvailable',event);
    event.stopPropagation();
    item.stockStatus = "available";
    item.details.currentStock = null;
    this.statusChanged.emit(item);
  }
  MakeOutOfStock(event:any,item: ProductModel): void {
    console.log('MakeOutOfStock',event);
    event.stopPropagation();
    item.stockStatus = "outOfStock";
    item.details.currentStock = 0;
    this.statusChanged.emit(item);
  }

  onQuantityChanged(quantity: number, item: ProductModel): void {
    if (!item.basket) {
      item.basket = {};
    }
    item.basket.quantity = quantity;
    item.basket.totalPriceVATIncluded = this.mathService.calculateProductTotalAmount(item);
    item.basket.totals = this.mathService.calculateProductTotalsAmount(item);
    this.quantityChanged.emit(item);
  }

  async onQuantityClicked(event:any,item:ProductModel){
    if(item.basket && item.basket.quantity>0){
      event.stopPropagation();
      const modal = await this.modalCtrl.create({
        component: ProductStatsComponent,
        cssClass: 'amount-editor',
        componentProps:{
          item:item,
        }
      });
      return await modal.present();      
    }
  }

  onIconClicked(product:ProductModel, icon:string,status:boolean):void{
    if(!this.canEdit){
      return;
    }
    console.log('onIconClicked');
    // if(!product.icons){
    product.icons = {
      favorite:false,
      new:false,
      discount:false
    }
    // };
    product.icons[icon] = status;
    this.iconUpdated.emit(product);
  }
}
